import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { parse } from 'lucene';

import { DatacenterType } from '../models/datacenter-aggrid-view';
import { DatacenterFiltersConfiguration } from '../datacenter/datacenter-filters-configuration';
import {
	SuggestiveListFilter,
	DatacenterCodeTranslationFilterType,
	DropdownFilter,
	SuggestiveListWithIdFilter,
	DocumentMillDropdownFilter,
	DocumentSuggestiveListFilterWithId,
	DocumentCodeWithIdFilterType
} from '../models/datacenter-filters';

import { DatacenterFilterApiService } from './datacenter-filter-api.service';

@Injectable({ providedIn: 'root' })
export class DatacenterLuceneFilterService {
	isLoadingFilters = false;
	private inFlightCodeTranslations = 0;
	constructor(
		private readonly defaultFilterService: DatacenterFiltersConfiguration,
		private readonly datacenterFilterApiService: DatacenterFilterApiService,
	) {}

	convertToFilterTypes(query: string, datacenterType: DatacenterType) {
		const filterConfiguration = this.defaultFilterService.getAllFilters(datacenterType);
		const ast = parse(query);

		const res = [];
		for (const filter of filterConfiguration) {
			const filterValues = filter.fromLuceneExpression(ast);
			if (filterValues.values.length === 0) {
				continue;
			}
			if (
				filter instanceof SuggestiveListFilter
				|| filter instanceof SuggestiveListWithIdFilter
				|| filter instanceof DropdownFilter
				|| filter instanceof DocumentMillDropdownFilter
				|| filter instanceof DocumentSuggestiveListFilterWithId
			) {
				if (filter instanceof DocumentSuggestiveListFilterWithId) {
					const suggestiveValues = <DocumentCodeWithIdFilterType[]> filterValues.values;
					this.populateDocumentCodes(filter, suggestiveValues)
						.catch((error: unknown) => {
							console.error('Failed to hydrate document suggestive codes', error);
						});
				} else {
					const suggestiveValues = <DatacenterCodeTranslationFilterType[]> filterValues.values;
					this.populateCodeTranslations(filter, suggestiveValues)
						.catch((error: unknown) => {
							console.error('Failed to hydrate dropdown/suggestive translations', error);
						});
				}
			}
			res.push(filterValues);
		}

		return res;
	}

	private async populateDocumentCodes(
		filter: DocumentSuggestiveListFilterWithId,
		values: DocumentCodeWithIdFilterType[]
	) {
		const ids = Array.from(new Set(
			values
				.filter((value): value is DocumentCodeWithIdFilterType & { id: string } =>
					Boolean(value.id) && !value.code)
				.map(value => value.id)
		));
		if (ids.length === 0) {
			return;
		}

		this.inFlightCodeTranslations += 1;
		this.isLoadingFilters = true;
		try {
			const entries = await firstValueFrom(this.datacenterFilterApiService.getItemsByCodes(filter, ids));
			const codeById = new Map<string, string>();
			for (const item of entries) {
				if (item.id && item.code) {
					codeById.set(item.id, item.code);
				}
			}

			for (const value of values) {
				if (!value.id || value.code) {
					continue;
				}
				const code = codeById.get(value.id);
				if (code) {
					value.code = code;
				}
			}
		} finally {
			this.inFlightCodeTranslations = Math.max(0, this.inFlightCodeTranslations - 1);
			this.isLoadingFilters = this.inFlightCodeTranslations > 0;
		}
	}

	private async populateCodeTranslations(
		filter:
			| SuggestiveListFilter
			| SuggestiveListWithIdFilter
			| DropdownFilter
			| DocumentMillDropdownFilter,
		values: DatacenterCodeTranslationFilterType[]
	) {
		const codes = Array.from(new Set(
			values
				.filter((value): value is DatacenterCodeTranslationFilterType & { code: string } =>
					Boolean(value.code) && !value.translation)
				.map(value => value.code)
		));
		if (codes.length === 0 || !filter.apiNameCode) {
			return;
		}

		this.inFlightCodeTranslations += 1;
		this.isLoadingFilters = true;
		try {
			const entries = await firstValueFrom(this.datacenterFilterApiService.getItemsByCodes(filter, codes));
			const translationMap = new Map<string, string>();
			const extraFieldMap = new Map<string, string | undefined>();
			for (const item of entries) {
				if (!item.code) {
					continue;
				}
				if (item.translation) {
					translationMap.set(String(item.code), item.translation);
				}
				if (item.extraField !== undefined) {
					extraFieldMap.set(String(item.code), item.extraField);
				}
			}

			for (const value of values) {
				if (!value.code || value.translation) {
					continue;
				}
				const translation = translationMap.get(String(value.code));
				if (translation) {
					value.translation = translation;
				}
				if (extraFieldMap.has(String(value.code))) {
					value.extraField = extraFieldMap.get(String(value.code));
				}
			}
		} finally {
			this.inFlightCodeTranslations = Math.max(0, this.inFlightCodeTranslations - 1);
			this.isLoadingFilters = this.inFlightCodeTranslations > 0;
		}
	}
}
